import { useState, useEffect, useRef } from "react";
import deepEqual from "fast-deep-equal";
import { v4 as uuidV4 } from "uuid";

import { useSelector, useDispatch } from "react-redux";
import {
  selectMenuOpen,
  setHomeDefaultSort,
  setAllEntryDefaultSort,
  setEntryDefaultSort,
  setPatientDefaultSort,
  setEntryThumbnailDefaultSort,
} from "../../../redux/features/applicationState/applicationStateSlice";

// Icons
import { ChevronDownIcon } from "@heroicons/react/24/outline";

const sortArrayByKey = (arr, key, ascending = true) => {
  const arrCopy = [...arr]; // Copy the array

  return arrCopy.sort((a, b) => {
    if (a[key] instanceof Date || b[key] instanceof Date) {
      // Date comparison using getTime method
      if (ascending) {
        return new Date(a[key]).getTime() - new Date(b[key]).getTime();
      } else {
        return new Date(b[key]).getTime() - new Date(a[key]).getTime();
      }
    } else if (typeof a[key] === "string" || typeof b[key] === "string") {
      // String comparison
      if (ascending) {
        return a[key].localeCompare(b[key]);
      } else {
        return b[key].localeCompare(a[key]);
      }
    } else {
      // Numeric comparison
      if (ascending) {
        return a[key] - b[key];
      } else {
        return b[key] - a[key];
      }
    }
  });
};

const CategoryToggleButton = ({
  label,
  activeCategory,
  category,
  isAscendingOrder,
  onSortToggle,
}) => {
  return (
    <button
      className={`flex flex-row items-center justify-between w-full my-1 p-1 rounded-md
                   text-neutral-700 hover:text-neutral-600 active:text-neutral-900 text-start
                   border border-1 
                   ${
                     category === activeCategory
                       ? "border-neutral-300"
                       : "border-white"
                   }`}
      onClick={onSortToggle}
    >
      {label}

      <ChevronDownIcon
        className={`ml-2 h-5 w-5 flex-none transition-all duration-200 ${
          category === activeCategory && isAscendingOrder ? "rotate-180" : ""
        }`}
        aria-hidden="true"
      />
    </button>
  );
};

const SortingButtons = ({
  rowType,
  data,
  setData,
  defaultSort = { by: "dateModified", ascending: true },
  categoryMapping = {
    dateModified: "date_modified",
  },
  categories = [{ label: "Date Modified", by: "dateModified" }],
}) => {
  const menuOpenStatus = useSelector(selectMenuOpen);
  const [currentSort, setCurrentSort] = useState(defaultSort);
  const dispatch = useDispatch();
  const isInitialRender = useRef(true);
  const prevDataRef = useRef(data);
  const prevSortRef = useRef(currentSort);

  const defaultSortUpdater = {
    home: setHomeDefaultSort,
    allEntry: setAllEntryDefaultSort,
    entry: setEntryDefaultSort,
    patient: setPatientDefaultSort,
    entryThumbnail: setEntryThumbnailDefaultSort,
  }[rowType];

  const sortData = (by) => {
    const isSameCategory = currentSort.by === by;
    const ascending = isSameCategory ? !currentSort.ascending : true;

    setCurrentSort({ by, ascending });
    dispatch(defaultSortUpdater({ by, ascending }));
  };

  const sortDataArray = (sortCriteria) => {
    const key = categoryMapping[sortCriteria.by];
    const sortedData = sortArrayByKey(data, key, sortCriteria.ascending);
    setData(sortedData);
  };

  useEffect(() => {
    // The useEffect hook here is designed to handle sorting of the `data` array
    // whenever necessary based on different triggers: initial load, user action,
    // or external data updates.

    // Case 1: Initial Sort
    // This block checks if the component has just mounted, which is indicated by
    // `isInitialRender.current` being true. If it is the component's initial render
    // and `data` is not empty, we perform a sort using the default sorting criteria.
    // After the initial sort, we set `isInitialRender.current` to false to ensure that
    // this block does not execute again on subsequent renders.
    if (isInitialRender.current && data.length > 0) {
      sortDataArray(defaultSort);
      isInitialRender.current = false;
    }
    // Case 2: Sort on User Action
    // If the sorting criteria (`currentSort`) has changed, which typically happens
    // when the user selects a different sort option, we perform a sort with the new
    // criteria. This block does not run on the initial render because the initial sort
    // takes precedence.
    else if (prevSortRef.current !== currentSort) {
      sortDataArray(currentSort);
    }
    // Case 3: Sort on External Data Update
    // This block checks if the data has been updated externally after the initial render.
    // It uses a deep equality check because the data is an array of objects and we want
    // to sort only when there is a substantive change in the data, not just when the
    // reference changes. If there is a change detected, we sort the data using the current
    // sorting criteria.
    else if (
      !isInitialRender.current &&
      !deepEqual(prevDataRef.current, data)
    ) {
      sortDataArray(currentSort);
    }

    // Updates for References
    // Regardless of which case triggered the sort, we update `prevDataRef` and
    // `prevSortRef` with the current `data` and `currentSort`, respectively.
    // These updates are crucial to ensure that the useEffect logic above can
    // accurately determine when the next sort should occur.
    prevDataRef.current = data;
    prevSortRef.current = currentSort;

    // Dependencies
    // The hook's dependency array includes `data` and `currentSort`, which means
    // the hook will re-run whenever these values change.
  }, [data, currentSort]);

  return (
    <div
      className={`flex flex-col items-center justify-between w-full Xbg-blue-500 
                  ${
                    menuOpenStatus
                      ? "xl:flex-row space-y-2 xl:space-y-0 xl:space-x-2"
                      : "lg:flex-row space-y-2 lg:space-y-0 lg:space-x-2"
                  }`}
    >
      {categories.map((item, index) => (
        <CategoryToggleButton
          key={`${uuidV4()}-${index}`}
          label={item.label}
          activeCategory={currentSort.by}
          category={item.by}
          isAscendingOrder={currentSort.ascending}
          onSortToggle={() => sortData(item.by)}
        />
      ))}
    </div>
  );
};

export { SortingButtons };
